import { useMemo, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
    useParentRelationships,
    useCreateParentRelationship,
    useUpdateParentRelationship,
    useDeleteParentRelationship,
    type ParentRelationship,
} from '@/api/parentRelationships';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { FormField, Input } from '@/components/ui/FormField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
type StatusTab = 'Active' | 'Inactive' | 'All';
const STATUS_TABS: StatusTab[] = ['Active', 'Inactive', 'All'];

export function ParentRelationshipsPanel() {
    const { data, isLoading, isError, error } = useParentRelationships();
    const createM = useCreateParentRelationship();
    const updateM = useUpdateParentRelationship();
    const deleteM = useDeleteParentRelationship();

    const [tab, setTab] = useState<StatusTab>('Active');
    const [editing, setEditing] = useState<ParentRelationship | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<ParentRelationship | null>(null);

    const rows = useMemo(() => {
        if (!data) return [];
        const filtered =
            tab === 'All' ? data
            : tab === 'Active' ? data.filter((r) => r.active)
            : data.filter((r) => !r.active);
        return [...filtered].sort((a, b) => a.label.localeCompare(b.label));
    }, [data, tab]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-lg font-bold text-text">Client Relationships</h2>
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                    <Plus className="w-3.5 h-3.5" /> Add relationship
                </Button>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Pick-list for the relationship captured when a parent or guardian signs a
                session note, treatment plan, or portal signature. Historical signatures
                keep the label they were saved with.
            </p>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter relationships by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {STATUS_TABS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                role="tab"
                                aria-selected={tab === s}
                                onClick={() => setTab(s)}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors',
                                    tab === s ? 'bg-surface text-text shadow-xs font-medium' : 'text-text-muted hover:text-text',
                                )}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'relationship' : 'relationships'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load relationships</p>
                        <p className="text-xs text-text-subtle">{extractErrorMessage(error, 'Please try again.')}</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No relationships match the current filter.' : 'No relationships yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Relationship</th>
                                    <th className="px-3 py-2 font-bold w-28">Status</th>
                                    <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                        <td className="px-3 py-2 font-medium">{r.label}</td>
                                        <td className="px-3 py-2"><StatusBadge status={r.active ? 'Active' : 'Inactive'} /></td>
                                        <td className="px-3 py-2 text-right">
                                            <Button variant="ghost" size="xs" aria-label={`Edit ${r.label}`} onClick={() => setEditing(r)} className="mr-1">
                                                <Pencil className="w-3 h-3" />
                                            </Button>
                                            <Button variant="ghost" size="xs" aria-label={`Delete ${r.label}`} onClick={() => setConfirmDelete(r)}>
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <RelationshipDialog
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {confirmDelete && (
                <DeleteConfirmDialog
                    row={confirmDelete}
                    onClose={() => setConfirmDelete(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirmDelete.id);
                        setConfirmDelete(null);
                    }}
                />
            )}
        </div>
    );
}

interface DialogPayload { label: string; active: boolean }

function RelationshipDialog({
    initial,
    onClose,
    onSubmit,
}: {
    initial: ParentRelationship | null;
    onClose: () => void;
    onSubmit: (p: DialogPayload) => Promise<void>;
}) {
    const [label, setLabel] = useState(initial?.label ?? '');
    const [active, setActive] = useState<boolean>(initial?.active ?? true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!label.trim()) { setErr('Relationship is required'); return; }
        setSaving(true);
        try {
            await onSubmit({ label: label.trim(), active });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit relationship' : 'Add relationship'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <FormField label="Relationship" required error={err ?? undefined}>
                        {(id) => <Input id={id} value={label} onChange={(e) => setLabel(e.target.value)} />}
                    </FormField>
                    <FormField label="Status">
                        {(id) => (
                            <select
                                id={id}
                                value={active ? 'Active' : 'Inactive'}
                                onChange={(e) => setActive(e.target.value === 'Active')}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        )}
                    </FormField>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function DeleteConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: ParentRelationship;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Delete relationship?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Delete <span className="font-medium text-text">{row.label}</span>? Historical
                    signatures that captured this relationship will keep the text they were
                    saved with.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try { await onConfirm(); }
                            catch (e) { setErr(extractErrorMessage(e, 'Could not delete.')); }
                            finally { setWorking(false); }
                        }}
                    >
                        {working ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
